// src/utils/excelGenerator.js
import * as XLSX from 'xlsx';


export const generateQuotationExcel = async (quotationData) => {
    try {
        console.log('🔄 Loading UNITECH template file...');
        
        // Load template from public folder
        const response = await fetch('../../pub');
        
        if (!response.ok) {
            throw new Error('Template file not found. Make sure invoice.xlsx is in the public folder.');
        }
        
        const arrayBuffer = await response.arrayBuffer();
        
        // Load the Excel template with all formatting preserved
        const workbook = XLSX.read(arrayBuffer, {
            type: 'array',
            cellStyles: true,
            cellFormulas: true,
            cellDates: true,
            cellNF: true,
            sheetStubs: true
        });
        
        console.log('✅ UNITECH template loaded with all formatting preserved');
        
        // Get the worksheet
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        
        // Fill in client data (exactly matching your template positions)
        // Row 11: Client Name in B11, Email in E11
        if (quotationData.clientName) {
            worksheet['B11'] = { 
                v: quotationData.clientName, 
                t: 's'
            };
        }
        
        if (quotationData.clientEmail) {
            worksheet['E11'] = { 
                v: quotationData.clientEmail, 
                t: 's'
            };
        }
        
        // Fill items starting from row 14 (as per your template)
        let currentRow = 14;
        let grandTotal = 0;
        
        quotationData.items.forEach((item, index) => {
            // Column A: Item No# (barcode)
            worksheet[`A${currentRow}`] = { 
                v: item.itemNo || item.barcode || '', 
                t: 's' 
            };
            
            // Column B: Description  
            worksheet[`B${currentRow}`] = { 
                v: item.description || '', 
                t: 's' 
            };
            
            // Column D: QTY
            worksheet[`D${currentRow}`] = { 
                v: item.quantity || 0, 
                t: 'n' 
            };
            
            // Column E: Unit Price
            worksheet[`E${currentRow}`] = { 
                v: item.unitPrice || 0, 
                t: 'n' 
            };
            
            // Column F: Total Price
            const totalPrice = (item.quantity || 0) * (item.unitPrice || 0);
            worksheet[`F${currentRow}`] = { 
                v: totalPrice, 
                t: 'n' 
            };
            
            grandTotal += totalPrice;
            currentRow++;
        });
        
        // Add grand total row (after all items)
        const totalRow = currentRow + 1;
        worksheet[`E${totalRow}`] = { 
            v: 'GRAND TOTAL:', 
            t: 's'
        };
        worksheet[`F${totalRow}`] = { 
            v: grandTotal, 
            t: 'n'
        };
        
        // Add quotation details at the bottom
        const infoStartRow = totalRow + 3;
        worksheet[`A${infoStartRow}`] = { v: 'Quotation #:', t: 's' };
        worksheet[`B${infoStartRow}`] = { v: quotationData.quotationNumber || '', t: 's' };
        worksheet[`D${infoStartRow}`] = { v: 'Date:', t: 's' };
        worksheet[`E${infoStartRow}`] = { v: new Date().toLocaleDateString(), t: 's' };
        
        worksheet[`A${infoStartRow + 1}`] = { v: 'Project:', t: 's' };
        worksheet[`B${infoStartRow + 1}`] = { v: quotationData.projectName || '', t: 's' };
        worksheet[`D${infoStartRow + 1}`] = { v: 'Valid Until:', t: 's' };
        worksheet[`E${infoStartRow + 1}`] = { v: getValidityDate(quotationData.createdAt, quotationData.validityDays), t: 's' };
        
        console.log('✅ All quotation data filled into template');
        
        return workbook;
        
    } catch (error) {
        console.error('❌ Error processing template:', error);
        throw error;
    }
};

// Helper function to calculate validity date
const getValidityDate = (createdAt, validityDays) => {
    if (!createdAt || !validityDays) return '';
    const created = new Date(createdAt);
    const validity = new Date(created);
    validity.setDate(validity.getDate() + validityDays);
    return validity.toLocaleDateString();
};

// Download function
export const downloadQuotationExcel = async (quotationData) => {
    try {
        console.log('🚀 Generating UNITECH quotation Excel...');
        
        const workbook = await generateQuotationExcel(quotationData);
        
        // Generate filename with UNITECH branding
        const safeQuotationNumber = (quotationData.quotationNumber || 'Draft').replace(/[^a-zA-Z0-9-_]/g, '_');
        const filename = `UNITECH_Commercial_Invoice_${safeQuotationNumber}_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        // Write to buffer preserving all formatting
        const excelBuffer = XLSX.write(workbook, { 
            bookType: 'xlsx', 
            type: 'array',
            compression: true,
            bookSST: false // Preserve shared strings and formatting
        });
        
        // Create blob and download
        const blob = new Blob([excelBuffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ UNITECH Excel downloaded successfully:', filename);
        
        return { success: true, filename };
        
    } catch (error) {
        console.error('❌ Excel download failed:', error);
        return { success: false, error: error.message };
    }
};